import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'expo-router';
import { AlertCircleIcon } from 'lucide-react-native';
import { Controller, useForm } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { z } from 'zod';

import { Header } from '@/components/header';
import { Heading } from '@/components/heading';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Input, InputField } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import UnauthenticatedLayout from '@/components/unauthenticatedLayout';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useColors } from '@/hooks/useColors';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { useToaster } from '@/hooks/useToaster';
import { trackForgotPasswordRequested } from '@/util/analyticsEvents';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email({
    message: 'Invalid email address',
  }),
});

function Content() {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(schema),
  });

  const { forgotPassword } = useAuth();

  const toast = useToaster();

  const { replace } = useRouter();

  const { appendRedirectPath } = useRedirectPath();

  const onSubmit = handleSubmit(async ({ email }) => {
    try {
      await forgotPassword(email);

      trackForgotPasswordRequested(email);

      replace(appendRedirectPath(`/forgot-password/success?email=${encodeURIComponent(email)}`));
    } catch (error) {
      console.error('ForgotPassword ERROR:', error);

      toast.showError({
        title: 'There was an issue with your submission. Please try again.',
      });
    }
  });

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  return (
    <VStack
      className="flex-1"
      style={{
        paddingBottom: bottom,
      }}>
      <Header
        contentScrollsUnderHeader
        headerShown={!isWeb}
        isBackButton
        backRoute={ROUTES.LOGIN}
        title="Forgot Password"
        statusBarBackgroundColor={colors.background[0]}
      />

      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        contentContainerClassName="flex-col flex-grow px-5 py-6">
        <VStack className="flex-1 web:mb-8">
          {isWeb ? (
            <Heading size="h1-xl" className="mb-2">
              Forgot Password
            </Heading>
          ) : null}

          <Text className="mb-8">
            Please enter your email address and we will send you instructions on how to reset
            password.
          </Text>

          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControl isInvalid={!!errors.email?.message}>
                <FormControlLabel className="mb-2">
                  <FormControlLabelText>Email*</FormControlLabelText>
                </FormControlLabel>

                <Input>
                  <InputField
                    autoCorrect={false}
                    placeholder="Enter email address"
                    placeholderTextColor={colors.typography[600]}
                    style={{ color: colors.typography[900] }}
                    className="text-base"
                    value={value}
                    onChangeText={(text: string) => onChange(text.toLowerCase())}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    enterKeyHint="done"
                    onSubmitEditing={onSubmit}
                  />
                </Input>

                <FormControlError className="mt-2">
                  <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                  <FormControlErrorText size="xs">{errors.email?.message}</FormControlErrorText>
                </FormControlError>
              </FormControl>
            )}
          />
        </VStack>

        <Button className="gap-x-2" onPress={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}

          <ButtonText>Submit</ButtonText>
        </Button>
      </KeyboardAwareScrollView>
    </VStack>
  );
}

export default function ForgotPassword() {
  const { appendRedirectPath } = useRedirectPath();

  const backPath = appendRedirectPath(ROUTES.LOGIN);

  const { navigateBack } = useBackNavigation(backPath);

  if (isWeb) {
    return (
      <UnauthenticatedLayout onBack={navigateBack}>
        <Content />
      </UnauthenticatedLayout>
    );
  }

  return <Content />;
}
